import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import {
  Plus, PencilSimple, PaperPlaneTilt, CircleNotch, ArrowClockwise,
  Globe, ShieldCheck, Heartbeat, Lightning, Key, Trash,
  CheckCircle, XCircle, Clock, Warning, Eye, Plugs,
} from '@phosphor-icons/react';

const STATUS_CONFIG = {
  draft: { bg: 'bg-[var(--surface-2)]', text: 'text-[#A0AEC0]', border: 'border-[var(--border-subtle)]', label: 'Draft', icon: PencilSimple },
  submitted: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Submitted', icon: Clock },
  in_review: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', label: 'In Review', icon: Eye },
  approved: { bg: 'bg-cyan-500/15', text: 'text-cyan-400', border: 'border-cyan-500/30', label: 'Approved', icon: CheckCircle },
  active: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Active', icon: CheckCircle },
  rejected: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'Rejected', icon: XCircle },
  suspended: { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30', label: 'Suspended', icon: Warning },
};

const HEALTH_STYLES = {
  healthy: 'text-emerald-400', degraded: 'text-yellow-400',
  unreachable: 'text-red-400', unknown: 'text-[#718096]',
};

function MyExternalAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [capabilities, setCapabilities] = useState([]);

  const fetchMyAgents = useCallback(async () => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/submissions/mine?limit=100`);
      if (res.ok) {
        const data = await res.json();
        setAgents(data.items || []);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchCapabilities = useCallback(async () => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/capabilities`);
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.capabilities || [];
        setCapabilities(list.map((c) => c.id || c.capability_id || c.name || c));
      }
    } catch {}
  }, []);

  useEffect(() => { fetchMyAgents(); fetchCapabilities(); }, [fetchMyAgents, fetchCapabilities]);

  const handleEdit = (agent) => { setEditingAgent(agent); setShowForm(true); };
  const handleNew = () => { setEditingAgent(null); setShowForm(true); };
  const handleFormClose = () => { setShowForm(false); setEditingAgent(null); };
  const handleSaved = () => { handleFormClose(); fetchMyAgents(); };

  const handleSubmit = async (federationId) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/submissions/${federationId}/submit`, { method: 'POST' });
      if (res.ok) {
        toast.success('Submitted for review');
        fetchMyAgents();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Submission failed');
      }
    } catch { toast.error('Submission failed'); }
  };

  const handleDelete = async (federationId) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/delete/${federationId}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Agent deleted'); fetchMyAgents(); }
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="my-external-agents" className="min-h-screen bg-[var(--surface-0)] px-6 py-8 lg:px-10">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              My External Agents
            </h1>
            <p className="text-sm text-[#718096] mt-1">
              Register and manage your external agent submissions
            </p>
          </div>
          <button
            data-testid="register-agent-btn"
            onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-gray-100 transition-colors"
          >
            <Plus size={16} weight="bold" />
            Register Agent
          </button>
        </div>

        {/* Agent List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={24} className="animate-spin text-[#718096]" />
          </div>
        ) : agents.length === 0 && !showForm ? (
          <div className="text-center py-20 bg-[var(--surface-1)] rounded-xl border border-[var(--border-subtle)]">
            <Globe size={40} className="mx-auto text-[#5C6778] mb-3" />
            <p className="text-white text-sm font-medium mb-1">No external agents yet</p>
            <p className="text-[#718096] text-xs mb-4">Register your first external agent to connect it to the platform</p>
            <button onClick={handleNew} className="text-xs px-3 py-1.5 rounded-lg bg-white text-black font-medium hover:bg-gray-100 transition-colors">
              Register Agent
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {agents.map((agent) => (
              <AgentRow
                key={agent.federation_id}
                agent={agent}
                onEdit={() => handleEdit(agent)}
                onSubmit={() => handleSubmit(agent.federation_id)}
                onDelete={() => handleDelete(agent.federation_id)}
              />
            ))}
          </div>
        )}

        {/* Registration / Edit Form */}
        {showForm && (
          <AgentForm
            agent={editingAgent}
            capabilities={capabilities}
            onClose={handleFormClose}
            onSaved={handleSaved}
          />
        )}
      </div>
    </div>
  );
}

function AgentRow({ agent, onEdit, onSubmit, onDelete }) {
  const sc = STATUS_CONFIG[agent.status] || STATUS_CONFIG.draft;
  const StatusIcon = sc.icon;
  const health = HEALTH_STYLES[agent.health_status] || HEALTH_STYLES.unknown;
  const caps = agent.canonical_capabilities || [];
  const canEdit = ['draft', 'rejected'].includes(agent.status);
  const canSubmit = ['draft', 'rejected'].includes(agent.status);
  const canDelete = ['draft', 'rejected'].includes(agent.status);

  return (
    <div data-testid={`agent-row-${agent.federation_id}`} className="p-4 rounded-xl bg-[var(--surface-1)] border border-[var(--border-subtle)] hover:border-[rgba(255,255,255,0.12)] transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-white truncate">{agent.name || 'Untitled Agent'}</h3>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${sc.bg} ${sc.text} ${sc.border} flex items-center gap-1`}>
              <StatusIcon size={10} />
              {sc.label}
            </span>
            <Heartbeat size={12} className={health} />
          </div>
          {agent.description && (
            <p className="text-xs text-[#A0AEC0] line-clamp-1 mb-2">{agent.description}</p>
          )}
          {/* Capabilities */}
          <div className="flex flex-wrap gap-1 mb-2">
            {caps.slice(0, 5).map((c) => (
              <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--surface-2)] border border-[var(--border-subtle)] text-[#A0AEC0]">
                {c}
              </span>
            ))}
            {caps.length > 5 && <span className="text-[10px] text-[#718096]">+{caps.length - 5}</span>}
          </div>
          {/* Rejection notes */}
          {agent.status === 'rejected' && agent.review_notes && (
            <div className="mt-2 p-2 rounded bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-red-400 font-medium mb-0.5">Rejection notes:</p>
              <p className="text-xs text-red-300/80">{agent.review_notes}</p>
            </div>
          )}
          <div className="flex items-center gap-3 text-[10px] text-[#718096] mt-1">
            <span>ID: {agent.federation_id}</span>
            {agent.developer_name && <span>Dev: {agent.developer_name}</span>}
            {agent.submitted_at && <span>Submitted: {new Date(agent.submitted_at).toLocaleDateString()}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-3">
          {canEdit && (
            <button data-testid={`edit-agent-${agent.federation_id}`} onClick={onEdit} className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.1)] text-[#718096] hover:text-white transition-colors" title="Edit">
              <PencilSimple size={16} />
            </button>
          )}
          {canSubmit && (
            <button data-testid={`submit-agent-${agent.federation_id}`} onClick={onSubmit} className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-400 text-xs font-medium hover:bg-blue-500/25 transition-colors" title="Submit for review">
              <PaperPlaneTilt size={12} />
              Submit
            </button>
          )}
          {canDelete && (
            <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#5C6778] hover:text-red-400 transition-colors" title="Delete">
              <Trash size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentForm({ agent, capabilities, onClose, onSaved }) {
  const isEdit = !!agent;
  const [form, setForm] = useState({
    name: agent?.name || '',
    description: agent?.description || '',
    endpoint_url: agent?.contract?.invoke?.endpoint_url || '',
    auth_method: agent?.auth_method || 'none',
    auth_key: '',
    auth_header: agent?.auth_config?.header_name || 'X-API-Key',
    declared_capabilities: agent?.canonical_capabilities || [],
    input_schema: JSON.stringify(agent?.contract?.invoke?.input_schema || {}, null, 2),
    output_schema: JSON.stringify(agent?.contract?.invoke?.output_schema || {}, null, 2),
    developer_name: agent?.developer_name || '',
    tags: (agent?.tags || []).join(', '),
    invoke_method: agent?.contract?.invoke?.method || 'POST',
    timeout_ms: agent?.contract?.invoke?.timeout_ms || 30000,
    health_url: agent?.contract?.health?.endpoint_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [capSearch, setCapSearch] = useState('');

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const toggleCap = (capId) => {
    setForm((prev) => ({
      ...prev,
      declared_capabilities: prev.declared_capabilities.includes(capId)
        ? prev.declared_capabilities.filter((c) => c !== capId)
        : [...prev.declared_capabilities, capId],
    }));
  };

  const filteredCaps = capabilities.filter((c) =>
    typeof c === 'string' && c.toLowerCase().includes(capSearch.toLowerCase())
  );

  const handleSave = async (submitNow = false) => {
    if (!form.name.trim()) { toast.error('Agent name is required'); return; }
    if (!form.endpoint_url.trim()) { toast.error('Endpoint URL is required'); return; }
    if (form.declared_capabilities.length === 0) { toast.error('Select at least one capability'); return; }

    let inputSchema = {}, outputSchema = {};
    try { inputSchema = JSON.parse(form.input_schema || '{}'); } catch { toast.error('Invalid input schema JSON'); return; }
    try { outputSchema = JSON.parse(form.output_schema || '{}'); } catch { toast.error('Invalid output schema JSON'); return; }

    setSaving(true);
    try {
      const body = {
        name: form.name,
        description: form.description,
        endpoint_url: form.endpoint_url,
        auth_method: form.auth_method,
        auth_config: form.auth_method === 'api_key'
          ? { api_key: form.auth_key || '***', header_name: form.auth_header }
          : form.auth_method === 'bearer' ? { token: form.auth_key || '***' } : {},
        declared_capabilities: form.declared_capabilities,
        input_schema: inputSchema,
        output_schema: outputSchema,
        developer_name: form.developer_name,
        invoke_method: form.invoke_method,
        timeout_ms: form.timeout_ms,
        health_url: form.health_url,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        submit_now: submitNow,
      };

      const method = isEdit ? 'PUT' : 'POST';
      const url = isEdit
        ? `${BACKEND_URL}/api/federation/submissions/${agent.federation_id}`
        : `${BACKEND_URL}/api/federation/submissions`;

      const res = await fetchWithOrg(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success(submitNow ? 'Submitted for review' : 'Draft saved');
        onSaved();
      } else {
        const err = await res.json();
        toast.error(err.detail?.error || err.detail || 'Save failed');
      }
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="agent-registration-form" className="relative w-full max-w-2xl max-h-[85vh] bg-[var(--surface-1)] border border-[var(--border-subtle)] rounded-2xl overflow-y-auto">
        <div className="sticky top-0 bg-[var(--surface-1)] border-b border-[var(--border-subtle)] px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">
              {isEdit ? 'Edit External Agent' : 'Register External Agent'}
            </h2>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.1)] text-[#718096]">
              <span className="text-lg">&times;</span>
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Agent Name *" value={form.name} onChange={(v) => update('name', v)} placeholder="My AI Agent" />
            <FormField label="Developer Name" value={form.developer_name} onChange={(v) => update('developer_name', v)} placeholder="Your name or org" />
          </div>
          <FormField label="Description" value={form.description} onChange={(v) => update('description', v)} placeholder="What does this agent do?" multiline />

          {/* Endpoint */}
          <div className="p-4 rounded-xl bg-[var(--surface-2)] border border-[var(--border-subtle)] space-y-3">
            <h3 className="text-xs font-medium text-[#718096] uppercase tracking-wider flex items-center gap-1.5">
              <Plugs size={12} /> Endpoint Configuration
            </h3>
            <FormField label="Invoke URL *" value={form.endpoint_url} onChange={(v) => update('endpoint_url', v)} placeholder="https://api.example.com/agent/invoke" />
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#718096] mb-1 block">Method</label>
                <select value={form.invoke_method} onChange={(e) => update('invoke_method', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)] text-sm text-white">
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                  <option value="PUT">PUT</option>
                </select>
              </div>
              <FormField label="Timeout (ms)" value={form.timeout_ms} onChange={(v) => update('timeout_ms', parseInt(v) || 30000)} type="number" />
              <FormField label="Health URL" value={form.health_url} onChange={(v) => update('health_url', v)} placeholder="/health" />
            </div>
          </div>

          {/* Auth */}
          <div className="p-4 rounded-xl bg-[var(--surface-2)] border border-[var(--border-subtle)] space-y-3">
            <h3 className="text-xs font-medium text-[#718096] uppercase tracking-wider flex items-center gap-1.5">
              <Key size={12} /> Authentication
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#718096] mb-1 block">Auth Type</label>
                <select value={form.auth_method} onChange={(e) => update('auth_method', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)] text-sm text-white">
                  <option value="none">None</option>
                  <option value="api_key">API Key</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="oauth">OAuth</option>
                </select>
              </div>
              {form.auth_method === 'api_key' && (
                <FormField label="Header Name" value={form.auth_header} onChange={(v) => update('auth_header', v)} placeholder="X-API-Key" />
              )}
            </div>
            {['api_key', 'bearer'].includes(form.auth_method) && (
              <FormField label={form.auth_method === 'api_key' ? 'API Key' : 'Bearer Token'} value={form.auth_key} onChange={(v) => update('auth_key', v)} placeholder="sk-..." type="password" />
            )}
          </div>

          {/* Capabilities */}
          <div className="p-4 rounded-xl bg-[var(--surface-2)] border border-[var(--border-subtle)] space-y-3">
            <h3 className="text-xs font-medium text-[#718096] uppercase tracking-wider flex items-center gap-1.5">
              <Lightning size={12} /> Capabilities * <span className="text-[10px] font-normal">(select from registry)</span>
            </h3>
            <input
              type="text"
              placeholder="Search capabilities..."
              value={capSearch}
              onChange={(e) => setCapSearch(e.target.value)}
              className="w-full px-3 py-1.5 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)] text-xs text-white placeholder-[#718096]"
            />
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {filteredCaps.slice(0, 40).map((c) => {
                const selected = form.declared_capabilities.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCap(c)}
                    className={`text-[11px] px-2 py-1 rounded-lg border transition-colors ${
                      selected
                        ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                        : 'bg-[var(--surface-1)] border-[var(--border-subtle)] text-[#718096] hover:text-white hover:border-[rgba(255,255,255,0.15)]'
                    }`}
                  >
                    {selected && <CheckCircle size={10} className="inline mr-0.5" />}
                    {c}
                  </button>
                );
              })}
            </div>
            {form.declared_capabilities.length > 0 && (
              <p className="text-[10px] text-blue-400">{form.declared_capabilities.length} selected</p>
            )}
          </div>

          {/* Schemas */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#718096] mb-1 block">Input Schema (JSON)</label>
              <textarea
                value={form.input_schema}
                onChange={(e) => update('input_schema', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] text-xs font-mono text-[#A0AEC0] placeholder-[#5C6778] resize-none"
                placeholder='{"type": "object", "properties": {}}'
              />
            </div>
            <div>
              <label className="text-xs text-[#718096] mb-1 block">Output Schema (JSON)</label>
              <textarea
                value={form.output_schema}
                onChange={(e) => update('output_schema', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] text-xs font-mono text-[#A0AEC0] placeholder-[#5C6778] resize-none"
                placeholder='{"type": "object", "properties": {}}'
              />
            </div>
          </div>

          {/* Tags */}
          <FormField label="Tags (comma-separated)" value={form.tags} onChange={(v) => update('tags', v)} placeholder="nlp, classification, external" />
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-[var(--surface-1)] border-t border-[var(--border-subtle)] px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-[#718096] hover:text-white transition-colors">Cancel</button>
          <button
            data-testid="save-draft-btn"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] text-sm text-white font-medium hover:bg-[rgba(255,255,255,0.1)] transition-colors disabled:opacity-50"
          >
            {saving ? <CircleNotch size={14} className="animate-spin inline mr-1" /> : null}
            Save Draft
          </button>
          <button
            data-testid="save-and-submit-btn"
            onClick={() => handleSave(true)}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <PaperPlaneTilt size={14} className="inline mr-1" />
            Save & Submit
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, multiline, type = 'text' }) {
  const cls = "w-full px-3 py-2 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)] text-sm text-white placeholder-[#718096] focus:outline-none focus:border-[#718096]";
  return (
    <div>
      {label && <label className="text-xs text-[#718096] mb-1 block">{label}</label>}
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} className={`${cls} resize-none`} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} />
      )}
    </div>
  );
}

export default MyExternalAgents;
